import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { homeFor } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const from = useLocation().state?.from;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={from || homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      nav(from || homeFor(u));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.15fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="/brand/slider3-1.jpg" alt="SCBB blood bike riders" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#111317]/55" />
        <div className="absolute inset-x-0 bottom-0 h-2 chevron-stripe" />
        <div className="on-photo relative flex h-full flex-col justify-end p-14">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-primary">Shropshire · Staffordshire · Cheshire</p>
          <h1 className="max-w-xl text-5xl font-extrabold leading-[1.05] tracking-tight text-white" data-testid="login-hero-title">SCBB Blood Bike Management App</h1>
          <p data-testid="login-hero-subtitle" className="mt-5 max-w-md text-base text-zinc-200">Assessments, Fleet Management, Rota &amp; Trustee Management System</p>
        </div>
      </div>

      <div className="relative flex items-center justify-center px-6 py-12">
        <ThemeToggle className="absolute right-4 top-4" testid="login-theme-toggle" />
        <form onSubmit={submit} className="rise-in w-full max-w-sm space-y-8" data-testid="login-form">
          <div className="space-y-5">
            <img src="/brand/logo.png" alt="SCBB logo" className="h-20 w-20" />
            <a href="/" data-testid="login-back-to-site" className="block text-xs font-semibold text-primary hover:underline">← Back to the public website</a>
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight">Sign in</h2>
              <p className="mt-1 text-sm text-muted-foreground">SCBB Blood Bike Management App</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" className="h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" className="h-12" />
            </div>
          </div>
          {error && <p data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-button" className="group flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground transition-shadow hover:shadow-[0_0_24px_rgba(254,220,25,0.4)] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
          </button>
          <p className="text-xs text-muted-foreground">Accounts are created by the Lead Assessor. Forgotten your password? Ask an administrator to reset it.</p>
        </form>
      </div>
    </div>
  );
}
